import React from 'react';

/**
 * Small shared pieces for the Partner Network screens.
 *
 * Kept in one file because each is a handful of lines and they are always
 * imported together; splitting them would be five imports for one table.
 */

/** The input style used across the existing admin console. */
export const inputClass =
  'w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500';

/** Compact variant for filter bars and table toolbars. */
export const filterClass =
  'rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500';

/* ------------------------------------------------------------------ */

interface BadgeProps {
  label: string;
  /** Tailwind border/bg/text triple from the status tone maps. */
  tone: string;
  className?: string;
}

export const StatusBadge: React.FC<BadgeProps> = ({ label, tone, className = '' }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${tone} ${className}`}
  >
    {label}
  </span>
);

/* ------------------------------------------------------------------ */

interface PipelineProps {
  stages: string[];
  labels: Record<string, string>;
  current: string;
  /** Rendered beside the track when the record has left the pipeline. */
  offTrackNote?: string;
}

/**
 * The stage track. Everything up to and including the current stage is filled,
 * so progress reads at a glance rather than needing the labels to be parsed.
 */
export const Pipeline: React.FC<PipelineProps> = ({
  stages,
  labels,
  current,
  offTrackNote,
}) => {
  const index = stages.indexOf(current);

  return (
    <div>
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-3">
        {stages.map((stage, position) => {
          const reached = index >= 0 && position <= index;
          const isCurrent = stage === current;

          return (
            <li key={stage} className="flex items-center gap-1">
              <div className="flex flex-col items-center gap-1.5 min-w-[74px]">
                <span
                  aria-hidden
                  className={`h-2.5 w-2.5 rounded-full ring-4 transition-colors ${
                    isCurrent
                      ? 'bg-primary-400 ring-primary-500/25'
                      : reached
                      ? 'bg-primary-500/70 ring-transparent'
                      : 'bg-surface-border ring-transparent'
                  }`}
                />
                <span
                  className={`text-center text-[10px] leading-tight ${
                    isCurrent
                      ? 'text-primary-400 font-medium'
                      : reached
                      ? 'text-muted'
                      : 'text-muted/50'
                  }`}
                >
                  {labels[stage]}
                </span>
              </div>
              {position < stages.length - 1 && (
                <span
                  aria-hidden
                  className={`h-px w-4 sm:w-6 -mt-5 ${
                    index > position ? 'bg-primary-500/60' : 'bg-surface-border'
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>

      {offTrackNote && (
        <p className="mt-4 text-xs text-amber-300/90">{offTrackNote}</p>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Wider shell for the compose and edit forms. */
  size?: 'md' | 'lg';
}

export const Modal: React.FC<ModalProps> = ({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
}) => {
  // A dialog over the page shouldn't leave the page scrolling underneath it.
  React.useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative w-full ${
          size === 'lg' ? 'max-w-3xl' : 'max-w-xl'
        } my-auto rounded-2xl border border-surface-border bg-surface-card shadow-xl animate-scale-in`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-surface-border px-6 py-5">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold text-white">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-muted">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-1 p-2 text-muted hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5">{children}</div>

        {footer && (
          <div className="flex flex-wrap justify-end gap-3 border-t border-surface-border px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface AlertProps {
  tone: 'error' | 'warning' | 'success' | 'info';
  children: React.ReactNode;
  className?: string;
}

const ALERT_TONES: Record<AlertProps['tone'], string> = {
  error: 'border-red-500/40 bg-red-500/10 text-red-400',
  warning: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  success: 'border-primary-500/40 bg-primary-500/10 text-primary-400',
  info: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
};

export const Alert: React.FC<AlertProps> = ({ tone, children, className = '' }) => (
  <div className={`rounded-lg border px-4 py-3 text-sm ${ALERT_TONES[tone]} ${className}`}>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */

export const Spinner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    role="status"
    aria-label="Loading"
    className={`animate-spin rounded-full border-b-2 border-primary-500 ${className || 'h-8 w-8'}`}
  />
);

interface EmptyStateProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  action,
}) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-surface-border px-6 py-14 text-center">
    <p className="text-white font-medium">{title}</p>
    {description && (
      <p className="mt-2 max-w-md text-sm text-muted">{description}</p>
    )}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

/* ------------------------------------------------------------------ */

interface PaginationProps {
  page: number;
  pages: number;
  total: number;
  onChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pages,
  total,
  onChange,
}) => {
  if (pages <= 1) {
    return (
      <p className="px-1 py-3 text-xs text-muted">
        {total} {total === 1 ? 'record' : 'records'}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3">
      <p className="text-xs text-muted">
        Page {page} of {pages} · {total} records
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="rounded-lg border border-surface-border px-3 py-1.5 text-sm text-muted transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= pages}
          className="rounded-lg border border-surface-border px-3 py-1.5 text-sm text-muted transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: 'default' | 'warning' | 'danger' | 'success';
  onClick?: () => void;
  active?: boolean;
}

const STAT_TONES: Record<NonNullable<StatCardProps['tone']>, string> = {
  default: 'text-white',
  warning: 'text-amber-300',
  danger: 'text-red-400',
  success: 'text-primary-400',
};

/** A dashboard tile. Clickable ones filter the list below them. */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  hint,
  tone = 'default',
  onClick,
  active = false,
}) => {
  const body = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
      <p className={`mt-2 font-display text-2xl font-semibold ${STAT_TONES[tone]}`}>
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </>
  );

  if (!onClick) {
    return (
      <div className="rounded-xl border border-surface-border bg-surface-card p-5">
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl border bg-surface-card p-5 text-left transition-colors ${
        active
          ? 'border-primary-500/50 bg-primary-500/5'
          : 'border-surface-border hover:border-primary-500/30'
      }`}
    >
      {body}
    </button>
  );
};
